/**
 * Builders turning decoded entity content into `PropertyDiffTable` rows — the
 * shared currency of the merge diff surfaces (compare preview, commit detail).
 * All equality here is structural (`differingKeys`), and a differing pair
 * whose rendered texts collide (a type-only change) is re-rendered with the
 * value kind spelled out.
 */
import type { DecodedValue } from '@axilon-apps/domains-ontology-ui-state'
import type { PropertyDiffRow } from '../DiffTable'
import {
  differingKeys,
  formatLogicalId,
  renderValue,
  renderValueDisambiguated
} from './entity-display'

/** Long values clip to a preview; the full value rides the title tooltip. */
export const VALUE_PREVIEW_MAX = 160

function clip(text: string): string {
  return text.length > VALUE_PREVIEW_MAX ? text.slice(0, VALUE_PREVIEW_MAX - 1) + '…' : text
}

/** A decoded property value for display, with composite relationship ids
 * rendered in the arrow form (see `formatLogicalId`). */
function displayValue(value: DecodedValue): string {
  return formatLogicalId(renderValue(value))
}

export interface ChangedPropertyRows {
  /** The properties this change touched, type-disambiguated where needed. */
  differing: PropertyDiffRow[]
  /** The untouched properties (identical before/after), for the noise fold. */
  unchanged: PropertyDiffRow[]
}

/**
 * Before/after rows for a changed entity: the structurally differing
 * properties (the actual change) split from the untouched remainder.
 */
export function changedPropertyRows(
  before: Record<string, DecodedValue>,
  after: Record<string, DecodedValue>
): ChangedPropertyRows {
  const differing = new Set(differingKeys(before, after))
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()
  const rows: ChangedPropertyRows = { differing: [], unchanged: [] }
  for (const key of keys) {
    const inBefore = key in before
    const inAfter = key in after
    if (!differing.has(key)) {
      rows.unchanged.push({
        name: key,
        after: inAfter ? clip(displayValue(after[key])) : undefined,
        afterTitle: inAfter ? renderValue(after[key]) : undefined
      })
      continue
    }
    // A differing pair whose sides render identically is a type-only change —
    // spell the kinds out so the difference is visible.
    const typeOnly = inBefore && inAfter && renderValue(before[key]) === renderValue(after[key])
    const render = (v: DecodedValue) =>
      clip(typeOnly ? renderValueDisambiguated(v) : displayValue(v))
    rows.differing.push({
      name: key,
      before: inBefore ? render(before[key]) : undefined,
      after: inAfter ? render(after[key]) : undefined,
      beforeTitle: inBefore ? renderValue(before[key]) : undefined,
      afterTitle: inAfter ? renderValue(after[key]) : undefined
    })
  }
  return rows
}

/** Value-mode rows listing an entity's full property set (adds / removes). */
export function propertyListingRows(props: Record<string, DecodedValue>): PropertyDiffRow[] {
  return Object.keys(props)
    .sort()
    .map((key) => ({
      name: key,
      after: clip(displayValue(props[key])),
      afterTitle: renderValue(props[key])
    }))
}

/** The synthetic labels row shown above property rows (display-only). */
export function labelsListingRow(labels: string[]): PropertyDiffRow {
  const text = [...labels].sort().join(', ')
  return { name: 'labels', after: text.length > 0 ? text : '(none)' }
}

/** The labels row of a changed node whose label sets diverge. */
export function labelsChangedRow(before: string[], after: string[]): PropertyDiffRow {
  const fmt = (labels: string[]) => {
    const text = [...labels].sort().join(', ')
    return text.length > 0 ? text : '(none)'
  }
  return { name: 'labels', before: fmt(before), after: fmt(after) }
}
